<template>
  <HeadlessListbox
    v-slot="{ open }"
    v-model="model"
    v-bind="$attrs"
    :multiple="multiple"
    :disabled="disabled"
    :class="{
      'min-w-64': !pills,
    }"
  >
    <div class="relative">
      <HeadlessListboxButton
        class="group text-left text-black outline-none focus:outline-none disabled:cursor-not-allowed disabled:bg-gray-10"
        :class="{
          'border-gray-300 text-white': dark,
          'border-gray-100 text-black hover:border-black': !dark,
          'border-red-600 bg-red-50': !!error,
          'flex items-center justify-between gap-6 rounded-full border px-4 py-2':
            pills,
          'w-full border-b px-3 pb-2 pr-8 pt-4 ': !pills,
        }"
        @focus="open = true"
      >
        <span
          class="pointer-events-none whitespace-nowrap font-medium transition-all"
          :class="{
            'absolute left-3 top-1 text-3xs': hasSelectedOptions && !pills,
            'top-3 text-sm': !hasSelectedOptions && !pills,
            'text-gray-300 group-hover:text-black': !error,
            'text-red-600': !!error,
            'text-xs': pills,
          }"
        >
          {{ label }} {{ required ? '*' : '' }}
        </span>
        <span v-if="hasSelectedOptions && !pills" class="text-black">
          {{ buttonText }}
        </span>
        <IconAngleDown
          class="pointer-events-none size-3 transition-all"
          :class="{
            'text-red-600': !!error,
            'rotate-180': open,
            'absolute right-2 top-5': !pills,
          }"
        />
      </HeadlessListboxButton>
      <Transition
        leave-active-class="transition duration-100 ease-in"
        leave-from-class="opacity-100"
        leave-to-class="opacity-0"
      >
        <HeadlessListboxOptions
          class="absolute z-40 min-w-full overflow-hidden whitespace-nowrap outline-none"
          :class="{
            'mt-2 rounded-lg': pills,
            'left-0': alignOptions === 'left',
            'right-0': alignOptions === 'right',
          }"
        >
          <HeadlessListboxOption
            v-for="option in options"
            :key="option.label"
            v-slot="{ selected }"
            :value="option.value"
          >
            <li
              class="relative flex cursor-pointer select-none items-center justify-between p-2 text-xs font-medium"
              :class="{
                'bg-black text-white': selected,
                'bg-white text-black hover:bg-black hover:text-white':
                  !selected,
              }"
            >
              <span>
                {{ option.label }}
              </span>
              <span v-if="selected" class="flex items-center pl-1">
                <IconCheck class="size-4" aria-hidden="true" />
              </span>
            </li>
          </HeadlessListboxOption>
        </HeadlessListboxOptions>
      </Transition>
      <span
        v-if="typeof error === 'string'"
        class="inline-flex px-3 text-4xs text-red-700"
      >
        {{ error }}
      </span>
    </div>
  </HeadlessListbox>
</template>

<script setup lang="ts">
type OptionValue = string | object | number
export type Option = { label: string; value: OptionValue }

const props = withDefaults(
  defineProps<{
    options: Option[]
    label: string
    icon?: 'string'
    error?: string | boolean
    dark?: boolean
    required?: boolean
    multiple?: boolean
    disabled?: boolean
    pills?: boolean
    alignOptions?: 'left' | 'right'
  }>(),
  {
    iconClasses: 'w-5 mx-2',
    required: true,
    multiple: true,
    dark: false,
    pills: false,
    disabled: false,
    alignOptions: 'left',
  }
)

const model = defineModel<OptionValue | OptionValue[]>()

const hasSelectedOptions = computed(() => {
  return Array.isArray(model.value) ? model.value.length > 0 : !!model.value
})

const getOptionLabel = (value: OptionValue) =>
  props.options.find((o) => JSON.stringify(o.value) === JSON.stringify(value))
    ?.label

const buttonText = computed(() => {
  if (props.multiple) {
    return hasSelectedOptions.value
      ? (model.value as OptionValue[]).map((o) => getOptionLabel(o)).join(', ')
      : props.label
  } else {
    return getOptionLabel(model.value as OptionValue) || props.label
  }
})
</script>
